import { useAuthStore } from '@xantory/store';
import { resolvePath } from './path';
import { authEndpoints } from '../domains/auth/auth.endpoints';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor() {
    super(401, 'UNAUTHORIZED', 'Session expired');
  }
}

/**
 * R-193: a 401 returned by a SITE, not by the cloud identity provider.
 *
 * Two genuinely different failures share one status code — "your session expired" and "this farm
 * does not recognise you" — and conflating them is how a farm-side authorisation gap destroyed
 * cloud sessions before R-191: every site 401 was read as "refresh me", so a healthy cloud token
 * was rotated for nothing and a failed refresh cleared a session that was never the problem.
 *
 * This is NOT a reason to sign the operator out: their cloud identity is fine and the rest of the
 * console still works. It IS a reason to stop showing that farm's last-known values as if they were
 * live.
 */
export class SiteUnauthorizedError extends ApiError {
  constructor() {
    super(401, 'SITE_UNAUTHORIZED', 'This site did not accept your session — its data is not live');
  }
}

interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip auth header injection (used for login/refresh/public endpoints) */
  noAuth?: boolean;
}

interface WrappedResponse<T> {
  data: T;
  message?: string;
}

/**
 * In-flight refreshes, keyed by ACCOUNT (R-303).
 *
 * This used to be one module-level `isRefreshing` flag plus one queue. With a
 * single session that was correct; holding several accounts at once it is not —
 * two accounts refreshing together would resolve each other's waiters and hand a
 * request the wrong account's access token.
 */
const refreshInFlight = new Map<string, Promise<string | null>>();

interface RefreshedPair {
  access_token: string;
  refresh_token: string;
  access_exp?: string;
  refresh_exp?: string;
}

/**
 * Rotate one account's tokens.
 *
 * ⚠ **A failed refresh is not automatically a dead session.** Rotation is
 * single-use on the backend (`rotate` matches the OLD refresh token), so when two
 * tabs — or the background refresher and a 401 retry — go at once, the loser
 * presents a token that was already spent and gets refused. The old code called
 * `clearSession()` on ANY non-OK response, which turned that lost race into a
 * sign-out. So before giving up we re-read the store: if the account's refresh
 * token has changed since we started, somebody else rotated successfully and
 * their new access token is the right answer.
 *
 * A thrown fetch (offline, DNS, the proxy down) is likewise NOT a dead session
 * and must never drop the account — the operator is simply offline.
 */
async function doRefresh(accountId: string): Promise<string | null> {
  const { accounts, setTokensFor, removeAccount } = useAuthStore.getState();
  const account = accounts[accountId];
  if (!account) return null;

  const startedWith = account.refreshToken;

  // The backend RefreshTokenForm requires BOTH tokens and rejects a mismatch (TokenMismatch) — send
  // the current access token alongside the refresh token, not the refresh token alone.
  let res: Response;
  try {
    res = await fetch(resolvePath(authEndpoints.refresh), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: account.accessToken, refresh_token: account.refreshToken }),
    });
  } catch {
    // Transport failure — the session may be perfectly alive. Keep the account.
    return null;
  }

  if (!res.ok) {
    // R-366: another TAB may have rotated this account since we read it — its write
    // reaches our memory only through the storage event, which can arrive after our
    // request went out. Re-read storage itself before judging, so a pair the browser
    // already holds is never mistaken for a dead session.
    try {
      await useAuthStore.persist.rehydrate();
    } catch {
      // Storage unreadable: judge on what we have.
    }
    const current = useAuthStore.getState().accounts[accountId];
    if (current && current.refreshToken !== startedWith) return current.accessToken; // lost the race, not expired

    // ⚠⚠ **ONLY A CREDENTIAL VERDICT ENDS A SESSION.** The backend refuses a refresh with
    // 401 (since R-237) or 400 (older gateways, still live on other tiers) — those two mean
    // the server read the credential and rejected it: an expired window, the 30-day ceiling,
    // a revoked user, a spent token. Everything else is the transport having a bad day.
    //
    // This used to sign the operator out on ANY 4xx, which made a DEPLOY a mass logout: the
    // SPA and the gateway are briefly different builds, a proxy re-resolves its upstream, and
    // `/users/auth/refresh` answers 404 or 405 for a few seconds. None of that is a statement
    // about the operator's session, and being signed out of a live farm console because a
    // router blinked is worse than one retry that fails. 5xx was already exempt for exactly
    // this reason (a restarting gateway); 404/405/408/429 belong in the same bucket and did
    // not get there.
    //
    // Erring this way is deliberate and asymmetric: keeping a session that IS dead costs one
    // more failed request, which the next 401 resolves. Dropping a session that is ALIVE
    // costs the operator their place, and on this console that can be a running crop.
    if (res.status !== 401 && res.status !== 400) return null;

    // Genuinely refused: expired window, the 30-day ceiling, a revoked user, a spent token.
    removeAccount(accountId);
    return null;
  }

  const json = (await res.json()) as WrappedResponse<RefreshedPair>;
  setTokensFor(accountId, json.data.access_token, json.data.refresh_token, {
    ...(json.data.access_exp ? { accessExp: json.data.access_exp } : {}),
    ...(json.data.refresh_exp ? { refreshExp: json.data.refresh_exp } : {}),
  });
  return json.data.access_token;
}

/**
 * Serialise refreshes for one account across TABS as well as within this one.
 *
 * Every tab of the console shares `localStorage`, so without a cross-tab lock two
 * tabs refresh the same account simultaneously and one of them always loses the
 * single-use rotation. `navigator.locks` is the only mechanism that spans tabs;
 * where it is unavailable the in-process map still prevents the same-tab race and
 * the re-read above absorbs the cross-tab one.
 */
async function withAccountLock<T>(accountId: string, fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) return fn();
  return locks.request(`xantory:auth:refresh:${accountId}`, fn);
}

/** Refresh one account at most once at a time. Exported so the background
 *  keepalive and the account switcher validate through the same path. */
export function refreshAccount(accountId: string): Promise<string | null> {
  const existing = refreshInFlight.get(accountId);
  if (existing) return existing;

  const run = withAccountLock(accountId, () => doRefresh(accountId)).finally(() => {
    refreshInFlight.delete(accountId);
  });
  refreshInFlight.set(accountId, run);
  return run;
}

/** Refresh whichever account is active (the 401-retry path). */
async function refreshOnce(): Promise<string | null> {
  const { activeId } = useAuthStore.getState();
  if (!activeId) return null;
  return refreshAccount(activeId);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, noAuth = false, headers: extraHeaders, ...rest } = options;
  const { accessToken } = useAuthStore.getState();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  if (!noAuth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  // String bodies with an explicit non-JSON Content-Type (e.g. text/csv bulk
  // imports, R-121) go through raw; everything else is JSON as before.
  const rawBody =
    typeof body === 'string' &&
    extraHeaders?.['Content-Type'] &&
    extraHeaders['Content-Type'] !== 'application/json';
  // R-227: a firmware image is BINARY. It must reach the server byte-for-byte —
  // JSON.stringify on a Uint8Array yields `{"0":31,"1":139,…}`, which would upload
  // a corrupt "image" the server then rejects with a confusing parse error.
  const binaryBody = body instanceof Uint8Array;
  if (binaryBody) headers['Content-Type'] = 'application/octet-stream';
  const serializedBody: BodyInit | null =
    body === undefined
      ? null
      : binaryBody
        ? // `.slice()` yields a plain ArrayBuffer, which BodyInit accepts. A
          // Uint8Array over a SharedArrayBuffer does not satisfy it, and the
          // difference is invisible until the type checker says so.
          ((body as Uint8Array).slice().buffer as ArrayBuffer)
        : rawBody
          ? (body as string)
          : JSON.stringify(body);

  // Resolve the logical path to the tier for the current mode once, then reuse for the 401 retry.
  const url = resolvePath(path);

  const res = await fetch(url, {
    ...rest,
    headers,
    ...(serializedBody !== null && { body: serializedBody }),
  });

  if (res.status === 401 && !noAuth) {
    // R-193: only a CLOUD-tier 401 means "my token is stale". A site-scoped URL (`/sites/{id}/…`)
    // was answered by the farm, whose verdict says nothing about the cloud session — refreshing
    // would rotate a healthy token, and a failed refresh would sign the operator out of everything
    // over one farm's authorisation problem.
    if (url.startsWith('/sites/')) throw new SiteUnauthorizedError();

    const newToken = await refreshOnce();
    if (!newToken) throw new UnauthorizedError();

    const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
    const retry = await fetch(url, {
      ...rest,
      headers: retryHeaders,
      ...(serializedBody !== null && { body: serializedBody }),
    });
    if (!retry.ok) await throwFromResponse(retry);
    if (retry.status === 204) return undefined as T;
    return ((await retry.json()) as WrappedResponse<T>).data as T;
  }

  if (!res.ok) await throwFromResponse(res);
  if (res.status === 204) return undefined as T;
  return ((await res.json()) as WrappedResponse<T>).data as T;
}

/** Turn a non-OK response into an `ApiError` carrying the server's message —
 *  exported for the few callers that need a raw `fetch` (file downloads). */
export async function throwFromResponse(res: Response): Promise<never> {
  let code = 'UNKNOWN';
  let message = res.statusText;
  try {
    const body = (await res.json()) as { error?: string; code?: string; message?: string };
    if (body.error) message = body.error;
    else if (body.message) message = body.message;
    if (body.code) code = body.code;
  } catch {
    /* not JSON */
  }
  // A proxy 502/503/504 means the service behind it is down/restarting — say
  // that instead of the bare statusText ("Bad Gateway" reads as gibberish to
  // an operator; their typed input is preserved by the calling form).
  if (code === 'UNKNOWN' && [502, 503, 504].includes(res.status)) {
    message = 'Service unreachable — your input is kept, please retry in a moment';
  }
  throw new ApiError(res.status, code, message);
}

/** Append non-empty params as a query string. Skips undefined/null/'' so callers pass sparse filters. */
export function withQuery(path: string, params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'body'>) => request<T>(path, { method: 'GET', ...opts }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: 'POST', body, ...opts }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: 'PUT', body, ...opts }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: 'PATCH', body, ...opts }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>(path, { method: 'DELETE', ...opts }),
  /** POST raw bytes (R-227 firmware images). Sent as application/octet-stream. */
  postRaw: <T>(path: string, body: Uint8Array, opts?: RequestOptions) =>
    request<T>(path, { method: 'POST', body, ...opts }),
};
